'use client';

import { Agent } from '@/types';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { CheckCircle2, ArrowRight } from 'lucide-react';

interface AgentSuggestionCardProps {
  agent: Agent;
  onSelect?: (agent: Agent) => void;
}

export function AgentSuggestionCard({ agent, onSelect }: AgentSuggestionCardProps) {
  const complexityColors = {
    low: 'success',
    medium: 'warning',
    high: 'info',
  } as const;

  const complexityLabels = {
    low: 'Semplice',
    medium: 'Medio',
    high: 'Avanzato',
  };

  return (
    <Card
      className="cursor-pointer transition-all hover:border-indigo-300 hover:shadow-md"
      onClick={() => onSelect?.(agent)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">{agent.name}</h4>
            <p className="mt-1 text-sm text-gray-500">{agent.description}</p>
          </div>
          <Badge variant={complexityColors[agent.complexity]}>
            {complexityLabels[agent.complexity]}
          </Badge>
        </div>

        <div className="mt-3 space-y-2">
          <h5 className="text-xs font-medium uppercase text-gray-400">
            Benefici principali
          </h5>
          <ul className="space-y-1">
            {agent.benefits.slice(0, 3).map((benefit, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {agent.estimatedROI && (
          <div className="mt-3 rounded-lg bg-indigo-50 p-2">
            <p className="text-sm font-medium text-indigo-700">
              {agent.estimatedROI}
            </p>
          </div>
        )}

        <div className="mt-3 flex items-center justify-end text-sm text-indigo-600">
          <span>Scopri di piu</span>
          <ArrowRight className="ml-1 h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}

interface AgentSuggestionsListProps {
  agents: Agent[];
  onSelect?: (agent: Agent) => void;
}

export function AgentSuggestionsList({ agents, onSelect }: AgentSuggestionsListProps) {
  if (agents.length === 0) return null;

  return (
    <div className="space-y-3 p-4">
      <h3 className="text-sm font-medium text-gray-700">
        Soluzioni consigliate per te
      </h3>
      <div className="grid gap-3">
        {agents.map((agent) => (
          <AgentSuggestionCard
            key={agent.id}
            agent={agent}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
